import { bcs, fromBase58, fromBase64, fromHex, toBase58, toBase64, toHex } from "@iota/bcs";
import {
  isValidIotaAddress,
  normalizeIotaAddress,
  IOTA_ADDRESS_LENGTH
} from "../utils/iota-types.js";
import { TypeTagSerializer } from "./type-tag-serializer.js";
function unsafe_u64(options) {
  return bcs.u64({
    name: "unsafe_u64",
    ...options
  }).transform({
    input: (val) => val,
    output: (val) => Number(val)
  });
}
function optionEnum(type) {
  return bcs.enum("Option", {
    None: null,
    Some: type
  });
}
const Address = bcs.bytes(IOTA_ADDRESS_LENGTH).transform({
  validate: (val) => {
    const address = typeof val === "string" ? val : toHex(val);
    if (!address || !isValidIotaAddress(normalizeIotaAddress(address))) {
      throw new Error(`Invalid IOTA address ${address}`);
    }
  },
  input: (val) => typeof val === "string" ? fromHex(normalizeIotaAddress(val)) : val,
  output: (val) => normalizeIotaAddress(toHex(val))
});
const ObjectDigest = bcs.byteVector().transform({
  name: "ObjectDigest",
  input: (value) => fromBase58(value),
  output: (value) => toBase58(new Uint8Array(value)),
  validate: (value) => {
    if (fromBase58(value).length !== 32) {
      throw new Error("ObjectDigest must be 32 bytes");
    }
  }
});
const IotaObjectRef = bcs.struct("IotaObjectRef", {
  objectId: Address,
  version: bcs.u64(),
  digest: ObjectDigest
});
const SharedObjectRef = bcs.struct("SharedObjectRef", {
  objectId: Address,
  initialSharedVersion: bcs.u64(),
  mutable: bcs.bool()
});
const ObjectArg = bcs.enum("ObjectArg", {
  ImmOrOwnedObject: IotaObjectRef,
  SharedObject: SharedObjectRef,
  Receiving: IotaObjectRef
});
const Owner = bcs.enum("Owner", {
  AddressOwner: Address,
  ObjectOwner: Address,
  Shared: bcs.struct("Shared", {
    initialSharedVersion: bcs.u64()
  }),
  Immutable: null
});
const CallArg = bcs.enum("CallArg", {
  Pure: bcs.struct("Pure", {
    bytes: bcs.byteVector().transform({
      input: (val) => typeof val === "string" ? fromBase64(val) : val,
      output: (val) => toBase64(new Uint8Array(val))
    })
  }),
  Object: ObjectArg
});
const InnerTypeTag = bcs.enum("TypeTag", {
  bool: null,
  u8: null,
  u64: null,
  u128: null,
  address: null,
  signer: null,
  vector: bcs.lazy(() => InnerTypeTag),
  struct: bcs.lazy(() => StructTag),
  u16: null,
  u32: null,
  u256: null
});
const TypeTag = InnerTypeTag.transform({
  input: (typeTag) => typeof typeTag === "string" ? TypeTagSerializer.parseFromStr(typeTag, true) : typeTag,
  output: (typeTag) => TypeTagSerializer.tagToString(typeTag)
});
const Argument = bcs.enum("Argument", {
  GasCoin: null,
  Input: bcs.u16(),
  Result: bcs.u16(),
  NestedResult: bcs.tuple([bcs.u16(), bcs.u16()])
});
const ProgrammableMoveCall = bcs.struct("ProgrammableMoveCall", {
  package: Address,
  module: bcs.string(),
  function: bcs.string(),
  typeArguments: bcs.vector(TypeTag),
  arguments: bcs.vector(Argument)
});
const Command = bcs.enum("Command", {
  /**
   * A Move Call - any public Move function can be called via
   * this transaction. The results can be used that instant to pass
   * into the next transaction.
   */
  MoveCall: ProgrammableMoveCall,
  /**
   * Transfer vector of objects to a receiver.
   */
  TransferObjects: bcs.struct("TransferObjects", {
    objects: bcs.vector(Argument),
    address: Argument
  }),
  // /**
  //  * Split `amount` from a `coin`.
  //  */
  SplitCoins: bcs.struct("SplitCoins", {
    coin: Argument,
    amounts: bcs.vector(Argument)
  }),
  // /**
  //  * Merge Vector of Coins (`sources`) into a `destination`.
  //  */
  MergeCoins: bcs.struct("MergeCoins", {
    destination: Argument,
    sources: bcs.vector(Argument)
  }),
  // /**
  //  * Publish a Move module.
  //  */
  Publish: bcs.struct("Publish", {
    modules: bcs.vector(
      bcs.byteVector().transform({
        input: (val) => typeof val === "string" ? fromBase64(val) : val,
        output: (val) => toBase64(new Uint8Array(val))
      })
    ),
    dependencies: bcs.vector(Address)
  }),
  // /**
  //  * Build a vector of objects using the input arguments.
  //  * It is impossible to export construct a `vector<T: key>` otherwise,
  //  * so this call serves a utility function.
  //  */
  MakeMoveVec: bcs.struct("MakeMoveVec", {
    type: optionEnum(TypeTag).transform({
      input: (val) => val === null ? {
        None: true
      } : {
        Some: val
      },
      output: (val) => val.Some ?? null
    }),
    elements: bcs.vector(Argument)
  }),
  Upgrade: bcs.struct("Upgrade", {
    modules: bcs.vector(
      bcs.byteVector().transform({
        input: (val) => typeof val === "string" ? fromBase64(val) : val,
        output: (val) => toBase64(new Uint8Array(val))
      })
    ),
    dependencies: bcs.vector(Address),
    package: Address,
    ticket: Argument
  })
});
const ProgrammableTransaction = bcs.struct("ProgrammableTransaction", {
  inputs: bcs.vector(CallArg),
  commands: bcs.vector(Command)
});
const MoveObjectType = bcs.enum("MoveObjectType", {
  Other: bcs.lazy(() => StructTag),
  GasCoin: null,
  StakedIota: null,
  Coin: InnerTypeTag
});
const MoveStruct = bcs.struct("MoveStruct", {
  objectType: MoveObjectType,
  version: bcs.u64(),
  contents: bcs.byteVector()
});
const MovePackage = bcs.struct("MovePackage", {
  id: Address,
  version: bcs.u64(),
  modules: bcs.map(bcs.string(), bcs.byteVector()),
  typeOriginTable: bcs.vector(
    bcs.struct("TypeOrigin", {
      moduleName: bcs.string(),
      datatypeName: bcs.string(),
      package: Address
    })
  ),
  linkageTable: bcs.map(
    Address,
    bcs.struct("UpgradeInfo", { upgradedId: Address, upgradedVersion: bcs.u64() })
  )
});
const ObjectData = bcs.enum("ObjectData", {
  Struct: MoveStruct,
  Package: MovePackage
});
const Event = bcs.struct("Event", {
  packageId: Address,
  module: bcs.string(),
  sender: Address,
  structTag: bcs.lazy(() => StructTag),
  contents: bcs.byteVector()
});
const GenesisTransaction = bcs.struct("GenesisTransaction", {
  objects: bcs.vector(
    bcs.enum("GenesisObject", {
      RawObject: bcs.struct("RawObject", { data: ObjectData, owner: Owner })
    })
  ),
  events: bcs.vector(Event)
});
const ConsensusCommitPrologueV1 = bcs.struct("ConsensusCommitPrologueV1", {
  epoch: bcs.u64(),
  round: bcs.u64(),
  subDagIndex: bcs.option(bcs.u64()),
  commitTimestampMs: bcs.u64(),
  consensusCommitDigest: ObjectDigest,
  consensusDeterminedVersionAssignments: bcs.enum("ConsensusDeterminedVersionAssignments", {
    CanceledTransactions: bcs.vector(
      bcs.struct("CanceledTransaction", {
        digest: ObjectDigest,
        versionAssignments: bcs.vector(
          bcs.struct("VersionAssignment", { objectId: Address, version: bcs.u64() })
        )
      })
    )
  })
});
const SystemPackage = bcs.struct("SystemPackage", {
  version: bcs.u64(),
  modules: bcs.vector(bcs.byteVector()),
  dependencies: bcs.vector(Address)
});
const ChangeEpoch = bcs.struct("ChangeEpoch", {
  epoch: bcs.u64(),
  protocolVersion: bcs.u64(),
  storageCharge: bcs.u64(),
  computationCharge: bcs.u64(),
  storageRebate: bcs.u64(),
  nonRefundableStorageFee: bcs.u64(),
  epochStartTimestampMs: bcs.u64(),
  systemPackages: bcs.vector(SystemPackage)
});
const ChangeEpochV2 = bcs.struct("ChangeEpochV2", {
  epoch: bcs.u64(),
  protocolVersion: bcs.u64(),
  storageCharge: bcs.u64(),
  computationCharge: bcs.u64(),
  computationChargeBurned: bcs.u64(),
  storageRebate: bcs.u64(),
  nonRefundableStorageFee: bcs.u64(),
  epochStartTimestampMs: bcs.u64(),
  systemPackages: bcs.vector(SystemPackage)
});
const ChangeEpochV3 = bcs.struct("ChangeEpochV3", {
  epoch: bcs.u64(),
  protocolVersion: bcs.u64(),
  storageCharge: bcs.u64(),
  computationCharge: bcs.u64(),
  computationChargeBurned: bcs.u64(),
  storageRebate: bcs.u64(),
  nonRefundableStorageFee: bcs.u64(),
  epochStartTimestampMs: bcs.u64(),
  systemPackages: bcs.vector(SystemPackage),
  eligibleActiveValidators: bcs.vector(bcs.u64())
});
const ChangeEpochV4 = bcs.struct("ChangeEpochV4", {
  epoch: bcs.u64(),
  protocolVersion: bcs.u64(),
  storageCharge: bcs.u64(),
  computationCharge: bcs.u64(),
  computationChargeBurned: bcs.u64(),
  storageRebate: bcs.u64(),
  nonRefundableStorageFee: bcs.u64(),
  epochStartTimestampMs: bcs.u64(),
  systemPackages: bcs.vector(SystemPackage),
  eligibleActiveValidators: bcs.vector(bcs.u64()),
  scores: bcs.vector(bcs.u64()),
  adjustRewardsByScore: bcs.bool()
});
const EndOfEpochTransactionKind = bcs.enum("EndOfEpochTransactionKind", {
  ChangeEpoch,
  ChangeEpochV2,
  ChangeEpochV3,
  ChangeEpochV4,
  TransactionDenyRulesCreate: null
});
const RandomnessStateUpdate = bcs.struct("RandomnessStateUpdate", {
  epoch: bcs.u64(),
  randomnessRound: bcs.u64(),
  randomBytes: bcs.byteVector(),
  randomnessObjInitialSharedVersion: bcs.u64()
});
const TransactionDenyRulesUpdate = bcs.struct("TransactionDenyRulesUpdate", {
  epoch: bcs.u64(),
  round: bcs.u64(),
  addedAddresses: bcs.vector(Address),
  removedAddresses: bcs.vector(Address),
  addedObjects: bcs.vector(Address),
  removedObjects: bcs.vector(Address),
  addedPackages: bcs.vector(Address),
  removedPackages: bcs.vector(Address),
  packagePublishDisabled: bcs.bool(),
  packageUpgradeDisabled: bcs.bool(),
  sharedObjectDisabled: bcs.bool(),
  userTransactionDisabled: bcs.bool(),
  receivingObjectsDisabled: bcs.bool(),
  moveAuthenticatorDisabled: bcs.bool(),
  denyRulesObjInitialSharedVersion: bcs.u64()
});
const TransactionKind = bcs.enum("TransactionKind", {
  ProgrammableTransaction,
  Genesis: GenesisTransaction,
  ConsensusCommitPrologueV1,
  AuthenticatorStateUpdateV1Deprecated: null,
  EndOfEpoch: bcs.vector(EndOfEpochTransactionKind),
  RandomnessStateUpdate,
  TransactionDenyRulesUpdate
});
const TransactionExpiration = bcs.enum("TransactionExpiration", {
  None: null,
  Epoch: unsafe_u64()
});
const StructTag = bcs.struct("StructTag", {
  address: Address,
  module: bcs.string(),
  name: bcs.string(),
  typeParams: bcs.vector(InnerTypeTag)
});
const GasData = bcs.struct("GasData", {
  payment: bcs.vector(IotaObjectRef),
  owner: Address,
  price: bcs.u64(),
  budget: bcs.u64()
});
const TransactionDataV1 = bcs.struct("TransactionDataV1", {
  kind: TransactionKind,
  sender: Address,
  gasData: GasData,
  expiration: TransactionExpiration
});
const TransactionData = bcs.enum("TransactionData", {
  V1: TransactionDataV1
});
const IntentScope = bcs.enum("IntentScope", {
  TransactionData: null,
  TransactionEffects: null,
  CheckpointSummary: null,
  PersonalMessage: null
});
const IntentVersion = bcs.enum("IntentVersion", {
  V0: null
});
const AppId = bcs.enum("AppId", {
  Iota: null
});
const Intent = bcs.struct("Intent", {
  scope: IntentScope,
  version: IntentVersion,
  appId: AppId
});
function IntentMessage(T) {
  return bcs.struct(`IntentMessage<${T.name}>`, {
    intent: Intent,
    value: T
  });
}
const CompressedSignature = bcs.enum("CompressedSignature", {
  ED25519: bcs.bytes(64),
  Secp256k1: bcs.bytes(64),
  Secp256r1: bcs.bytes(64)
});
const PublicKey = bcs.enum("PublicKey", {
  ED25519: bcs.bytes(32),
  Secp256k1: bcs.bytes(33),
  Secp256r1: bcs.bytes(33)
});
const MultiSigPkMap = bcs.struct("MultiSigPkMap", {
  pubKey: PublicKey,
  weight: bcs.u8()
});
const MultiSigPublicKey = bcs.struct("MultiSigPublicKey", {
  pk_map: bcs.vector(MultiSigPkMap),
  threshold: bcs.u16()
});
const MultiSig = bcs.struct("MultiSig", {
  sigs: bcs.vector(CompressedSignature),
  bitmap: bcs.u16(),
  multisig_pk: MultiSigPublicKey
});
const base64String = bcs.byteVector().transform({
  input: (val) => typeof val === "string" ? fromBase64(val) : val,
  output: (val) => toBase64(new Uint8Array(val))
});
const SenderSignedTransaction = bcs.struct("SenderSignedTransaction", {
  intentMessage: IntentMessage(TransactionData),
  txSignatures: bcs.vector(base64String)
});
const SenderSignedData = bcs.vector(SenderSignedTransaction, {
  name: "SenderSignedData"
});
const PasskeyAuthenticator = bcs.struct("PasskeyAuthenticator", {
  authenticatorData: bcs.byteVector(),
  clientDataJson: bcs.string(),
  userSignature: bcs.byteVector()
});
const MoveAuthenticatorV1 = bcs.struct("MoveAuthenticatorV1", {
  callArgs: bcs.vector(CallArg),
  typeArgs: bcs.vector(TypeTag),
  objectToAuthenticate: CallArg
});
const MoveAuthenticator = bcs.enum("MoveAuthenticator", {
  V1: MoveAuthenticatorV1
});
export {
  Address,
  AppId,
  Argument,
  CallArg,
  Command,
  CompressedSignature,
  GasData,
  Intent,
  IntentMessage,
  IntentScope,
  IntentVersion,
  IotaObjectRef,
  MoveAuthenticator,
  MultiSig,
  MultiSigPkMap,
  MultiSigPublicKey,
  ObjectArg,
  ObjectDigest,
  Owner,
  PasskeyAuthenticator,
  ProgrammableMoveCall,
  ProgrammableTransaction,
  PublicKey,
  SenderSignedData,
  SenderSignedTransaction,
  SharedObjectRef,
  StructTag,
  TransactionData,
  TransactionDataV1,
  TransactionExpiration,
  TransactionKind,
  TypeTag,
  base64String
};
//# sourceMappingURL=bcs.js.map
